import { useState, useEffect, useRef } from 'react'

interface LazyImageProps {
    src: string
    alt: string
    className?: string
    fallbackSrc?: string
}

export default function LazyImage({ src, alt, className = '', fallbackSrc }: LazyImageProps) {
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(false)
    const imageRef = useRef<HTMLImageElement>(null)
    const observerRef = useRef<IntersectionObserver | null>(null)

    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && imageRef.current) {
                        imageRef.current.src = src
                        observerRef.current?.unobserve(entry.target)
                    }
                })
            },
            {
                rootMargin: '50px',
            }
        )

        if (imageRef.current) {
            observerRef.current.observe(imageRef.current)
        }

        return () => {
            observerRef.current?.disconnect()
        }
    }, [src])

    return (
        <div className={`relative ${className}`}>
            <img
                ref={imageRef}
                alt={alt}
                src={error ? (fallbackSrc || '') : ''}
                className={`
                    transition-opacity duration-300
                    ${isLoaded ? 'opacity-100' : 'opacity-0'}
                    ${className}
                `}
                onLoad={() => setIsLoaded(true)}
                onError={() => {
                    setError(true)
                    setIsLoaded(true)
                }}
            />
            {!isLoaded && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse" />
            )}
        </div>
    )
} 